import {useHydrationDone} from '@aboutyou/lazy-imports';
import {useTrackOverlay} from '@aboutyou/tracking';
import {
  COLORS,
  ContentPopper,
  ContentPopperProps,
  DrawerBody,
  FlyoutContent,
  ModalDialogContent,
  Z_INDEXES,
} from '@aboutyou/ui-components';
import {styled} from '@linaria/react';
import React, {ComponentType, FC, useCallback} from 'react';
import coinsClaimSuccessBackground from '../../assets/coins-claim-success-background.svg';
import {CoinsClaimPopupContentError} from './Error/CoinsClaimPopupContentError';
import {CoinsClaimPopupContentSuccess} from './Success/CoinsClaimPopupContentSuccess';
import {useOpenCoinsClaimPopup} from './useOpenCoinsClaimPopup';

interface ClaimedCoinsPopupProps {
  CoinsExplosionAnimation: ComponentType<{showBodyContent: boolean}>;
  SuccessLayoutComponent: ContentPopperProps['LayoutComponent'];
  ErrorLayoutComponent: ContentPopperProps['LayoutComponent'];
}

export const ClaimedCoinsPopup: FC<ClaimedCoinsPopupProps> = ({
  CoinsExplosionAnimation,
  SuccessLayoutComponent,
  ErrorLayoutComponent,
}) => {
  const {isOpen, coinsData, cancel} = useOpenCoinsClaimPopup();

  const isHydrationDone = useHydrationDone();

  const trackOverlay = useTrackOverlay();

  const onImpression = useCallback(() => {
    if (coinsData?.result?.$case !== 'success') {
      return;
    }
    trackOverlay({
      action: 'impression',
      content_type: 'bottom_sheet',
      name: 'mission_completed',
      event_scope: 'long',
      object_type: 'mission',
      object_id: coinsData?.missionIdentifier.missionId,
      object_context: coinsData?.result?.success?.benefit?.reward ?? '',
    });
  }, [coinsData]);

  if (!isHydrationDone) {
    return null;
  }

  return (
    <StyledContentPopper
      data-testid={
        coinsData?.result?.$case === 'error'
          ? 'coinsClaimErrorPopup'
          : 'coinsClaimSuccessPopup'
      }
      LayoutComponent={
        coinsData?.result?.$case === 'error'
          ? ErrorLayoutComponent
          : SuccessLayoutComponent
      }
      data-error={coinsData?.result?.$case === 'error'}
      renderInPortal
      slideDirection="left"
      open={isOpen}
      preventSlideUpAnimation
      hideCloseButton
      onClose={cancel}
      bodyContent={
        <BodyContent
          CoinsExplosionAnimation={CoinsExplosionAnimation}
          coinsData={coinsData}
          isOpen={isOpen}
          onClose={cancel}
          onImpression={onImpression}
        />
      }
    />
  );
};

const BodyContent: FC<
  Pick<ClaimedCoinsPopupProps, 'CoinsExplosionAnimation'> & {
    coinsData: ReturnType<typeof useOpenCoinsClaimPopup>['coinsData'];
    isOpen: boolean;
    onClose: VoidFunction;
    onImpression: VoidFunction;
  }
> = ({coinsData, CoinsExplosionAnimation, isOpen, onClose, onImpression}) => {
  switch (coinsData?.result?.$case) {
    case 'success':
      return (
        <CoinsClaimPopupContentSuccess
          coinsData={coinsData.result.success}
          CoinsExplosionAnimation={CoinsExplosionAnimation}
          isOpen={isOpen}
          onClose={onClose}
          onImpression={onImpression}
        />
      );
    case 'error':
      return <CoinsClaimPopupContentError {...coinsData.result.error} />;
    default:
      return null;
  }
};

const backgroundImageStyles = `
  background-color: ${COLORS['Fill.Black']};
  background-image: linear-gradient(
      180deg,
      rgba(0, 0, 0, 0.85) 0%,
      rgba(0, 0, 0, 0.7) 30%
    ),
    url(${coinsClaimSuccessBackground});
  background-size: cover;
  background-position: center;
`;

const StyledContentPopper = styled(ContentPopper)`
  z-index: ${Z_INDEXES.COINS_CLAIM_POPUP};

  ${DrawerBody} {
    padding: 0;
    border-top: none;
    border-bottom: none;

    ${backgroundImageStyles}
  }

  ${FlyoutContent}:not([data-error='true']) {
    ${backgroundImageStyles}
  }

  &[data-error='true'] ${ModalDialogContent} {
    max-width: 375px;
  }
`;
